/**
 * VisitorTracker - localStorage-based tracking for popup display rules
 *
 * Tracks:
 * - Page views count (incremented on each page load)
 * - First visit timestamp
 * - Which popups have been shown (and when)
 * - Session information
 *
 * Used to evaluate popup display conditions like:
 * - page_views >= N
 * - visitor_type == 'new' | 'returning'
 * - popup not already shown
 * - time since first visit
 */

export interface PopupShowRecordEntry {
  lastShown: number;      // timestamp when last shown
  showCount: number;      // how many times shown (for future use)
  sessionId?: string;     // session ID when shown (for per_session frequency)
}

export interface PopupShowRecord {
  [popupId: string]: PopupShowRecordEntry | number; // new format or legacy timestamp
}

export interface PopupFrequency {
  type: 'once' | 'per_session' | 'reset_after_days';
  reset_days?: number;
}

export interface VisitorData {
  pageViews: number;
  firstVisit: number;
  lastVisit: number;
  sessionStart: number;
  popupsShown: PopupShowRecord;
}

export interface PopupCondition {
  type: string;
  operator?: string;
  value?: string | number | boolean;
}

export interface FlowNode {
  id: string;
  type: string;
  operator?: string;
  value?: string | number;
  data?: Record<string, any>;
  position?: { x: number; y: number };
}

export interface FlowEdge {
  id?: string;
  source: string;
  target: string;
  label?: string;
}

export interface DisplayFlow {
  nodes: FlowNode[];
  edges: FlowEdge[];
}

export interface PopupConfig {
  id: string;
  name: string;
  type: string;
  position?: string;
  html_content?: string;
  css_custom?: string;
  cta_config?: Record<string, any>;
  theme?: Record<string, any>;
  display_flow?: DisplayFlow;
  priority?: number;
  frequency?: PopupFrequency;
  background_image?: string;
  images?: Record<string, string | { url: string; alt?: string }>;
}

const STORAGE_KEYS = {
  PAGE_VIEWS: 'qc_page_views',
  FIRST_VISIT: 'qc_first_visit',
  LAST_VISIT: 'qc_last_visit',
  SESSION_START: 'qc_session_start',
  POPUPS_SHOWN: 'qc_popups_shown',
};

// Session timeout in milliseconds (30 minutes)
const SESSION_TIMEOUT = 30 * 60 * 1000;

export class VisitorTracker {
  private siteId: string;
  private data: VisitorData;
  private debug: boolean;
  private sessionId: string;

  constructor(siteId: string, debug: boolean = false) {
    this.siteId = siteId;
    this.debug = debug;
    this.sessionId = this.getOrCreateSessionId();
    this.data = this.loadData();
    this.trackPageView();
  }

  /**
   * Generate or retrieve session ID from sessionStorage
   * Session ID is cleared when browser/tab is closed
   */
  private getOrCreateSessionId(): string {
    const key = `qc_session_id_${this.siteId}`;
    let sessionId = sessionStorage.getItem(key);

    if (!sessionId) {
      sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem(key, sessionId);
      this.log(`New session created: ${sessionId}`);
    }

    return sessionId;
  }

  /**
   * Get current session ID
   */
  public getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Load visitor data from localStorage
   */
  private loadData(): VisitorData {
    const now = Date.now();

    const pageViews = parseInt(localStorage.getItem(this.key(STORAGE_KEYS.PAGE_VIEWS)) || '0', 10);
    const firstVisit = parseInt(localStorage.getItem(this.key(STORAGE_KEYS.FIRST_VISIT)) || '0', 10) || now;
    const lastVisit = parseInt(localStorage.getItem(this.key(STORAGE_KEYS.LAST_VISIT)) || '0', 10) || now;
    const sessionStart = parseInt(localStorage.getItem(this.key(STORAGE_KEYS.SESSION_START)) || '0', 10);

    let popupsShown: PopupShowRecord = {};
    try {
      const stored = localStorage.getItem(this.key(STORAGE_KEYS.POPUPS_SHOWN));
      if (stored) {
        const parsed = JSON.parse(stored);
        // Migrate old format to new format if needed
        popupsShown = this.migratePopupsShownData(parsed);
      }
    } catch (e) {
      this.log('Failed to parse popups_shown from localStorage', e);
    }

    // Check if this is a new session (last visit was more than SESSION_TIMEOUT ago)
    const isNewSession = (now - lastVisit) > SESSION_TIMEOUT;
    const currentSessionStart = isNewSession ? now : (sessionStart || now);

    return {
      pageViews,
      firstVisit,
      lastVisit,
      sessionStart: currentSessionStart,
      popupsShown,
    };
  }

  /**
   * Get storage key with site prefix
   */
  private key(baseKey: string): string {
    return `${baseKey}_${this.siteId}`;
  }

  /**
   * Track page view - called on every page load
   */
  private trackPageView(): void {
    const now = Date.now();

    // Increment page views
    this.data.pageViews += 1;

    // Update first visit if not set
    if (!this.data.firstVisit) {
      this.data.firstVisit = now;
    }

    // Update last visit
    this.data.lastVisit = now;

    // Persist to localStorage
    this.saveData();

    this.log(`Page view tracked. Total: ${this.data.pageViews}`);
  }

  /**
   * Save visitor data to localStorage
   */
  private saveData(): void {
    localStorage.setItem(this.key(STORAGE_KEYS.PAGE_VIEWS), this.data.pageViews.toString());
    localStorage.setItem(this.key(STORAGE_KEYS.FIRST_VISIT), this.data.firstVisit.toString());
    localStorage.setItem(this.key(STORAGE_KEYS.LAST_VISIT), this.data.lastVisit.toString());
    localStorage.setItem(this.key(STORAGE_KEYS.SESSION_START), this.data.sessionStart.toString());
    localStorage.setItem(this.key(STORAGE_KEYS.POPUPS_SHOWN), JSON.stringify(this.data.popupsShown));
  }

  /**
   * Migrate old localStorage format to new format
   * Old: { "popup-id": 1702900000000 }
   * New: { "popup-id": { lastShown: 1702900000000, showCount: 1, sessionId: undefined } }
   */
  private migratePopupsShownData(data: any): PopupShowRecord {
    const migrated: PopupShowRecord = {};

    for (const [popupId, value] of Object.entries(data)) {
      if (typeof value === 'number') {
        // Old format - migrate
        migrated[popupId] = {
          lastShown: value,
          showCount: 1,
          sessionId: undefined,
        };
        this.log(`Migrated popup ${popupId} from old format`);
      } else if (typeof value === 'object' && value !== null && 'lastShown' in (value as any)) {
        // Already new format
        migrated[popupId] = value as PopupShowRecordEntry;
      }
    }

    return migrated;
  }

  /**
   * Get popup show record (normalized to new format)
   */
  private getPopupRecord(popupId: string): PopupShowRecordEntry | null {
    const record = this.data.popupsShown[popupId];
    if (!record) return null;

    // Handle legacy format (just timestamp)
    if (typeof record === 'number') {
      return {
        lastShown: record,
        showCount: 1,
        sessionId: undefined,
      };
    }

    return record;
  }

  /**
   * Mark a popup as shown (enhanced version with session tracking)
   */
  public markPopupShown(popupId: string): void {
    const existingRecord = this.getPopupRecord(popupId);
    const newRecord: PopupShowRecordEntry = {
      lastShown: Date.now(),
      showCount: (existingRecord?.showCount || 0) + 1,
      sessionId: this.sessionId,
    };

    this.data.popupsShown[popupId] = newRecord;
    this.saveData();
    this.log(`Popup ${popupId} marked as shown (session: ${this.sessionId}, count: ${newRecord.showCount})`);
  }

  /**
   * Check if popup should be shown based on frequency settings
   * This is the main method for frequency-based display control
   */
  public shouldShowPopup(popupId: string, frequency?: PopupFrequency): boolean {
    const record = this.getPopupRecord(popupId);

    // Never shown before - always show
    if (!record) {
      this.log(`Popup ${popupId} never shown before, should show`);
      return true;
    }

    const frequencyType = frequency?.type || 'once';

    switch (frequencyType) {
      case 'once':
        // Already shown - never show again
        this.log(`Popup ${popupId} frequency=once, already shown, should NOT show`);
        return false;

      case 'per_session':
        // Show once per browser session
        const shownInThisSession = record.sessionId === this.sessionId;
        this.log(`Popup ${popupId} frequency=per_session, shownInThisSession=${shownInThisSession}, recordSession=${record.sessionId}, currentSession=${this.sessionId}`);
        return !shownInThisSession;

      case 'reset_after_days':
        // Show if enough days have passed since last shown
        const resetDays = frequency?.reset_days || 7;
        const daysSinceShown = (Date.now() - record.lastShown) / (1000 * 60 * 60 * 24);
        const canShow = daysSinceShown >= resetDays;
        this.log(`Popup ${popupId} frequency=reset_after_days(${resetDays}), daysSinceShown=${daysSinceShown.toFixed(2)}, canShow=${canShow}`);
        return canShow;

      default:
        this.log(`Unknown frequency type: ${frequencyType}, treating as 'once'`);
        return false;
    }
  }

  /**
   * Check if a popup has been shown (legacy method)
   * @deprecated Use shouldShowPopup() with frequency instead
   */
  public wasPopupShown(popupId: string): boolean {
    return popupId in this.data.popupsShown;
  }

  /**
   * Get when a popup was last shown (timestamp or null)
   */
  public getPopupShownTime(popupId: string): number | null {
    const record = this.getPopupRecord(popupId);
    return record?.lastShown || null;
  }

  /**
   * Get full popup show info (for debugging)
   */
  public getPopupShowInfo(popupId: string): PopupShowRecordEntry | null {
    return this.getPopupRecord(popupId);
  }

  /**
   * Get current page view count
   */
  public getPageViews(): number {
    return this.data.pageViews;
  }

  /**
   * Get visitor type: 'new' (first page view) or 'returning'
   */
  public getVisitorType(): 'new' | 'returning' {
    return this.data.pageViews <= 1 ? 'new' : 'returning';
  }

  /**
   * Check if this is a new session
   */
  public isNewSession(): boolean {
    const now = Date.now();
    return (now - this.data.sessionStart) < 1000; // Within 1 second of session start
  }

  /**
   * Get time since first visit in milliseconds
   */
  public getTimeSinceFirstVisit(): number {
    return Date.now() - this.data.firstVisit;
  }

  /**
   * Get time since session start in milliseconds
   */
  public getSessionDuration(): number {
    return Date.now() - this.data.sessionStart;
  }

  /**
   * Evaluate a single condition
   */
  public evaluateCondition(condition: PopupCondition): boolean {
    const { type, operator, value } = condition;

    switch (type) {
      case 'page_views': {
        const pageViews = this.getPageViews();
        const targetValue = parseInt(String(value), 10);
        return this.compareValues(pageViews, operator || '>=', targetValue);
      }

      case 'visitor_type': {
        const visitorType = this.getVisitorType();
        return visitorType === value;
      }

      case 'time_on_page': {
        // Time on page in seconds
        const timeOnPage = this.getSessionDuration() / 1000;
        const targetSeconds = parseInt(String(value), 10);
        return this.compareValues(timeOnPage, operator || '>=', targetSeconds);
      }

      case 'scroll_depth': {
        // This will be evaluated dynamically by the widget, not here
        // Return true to allow other conditions to determine
        return true;
      }

      case 'exit_intent': {
        // This will be evaluated dynamically by the widget
        return true;
      }

      case 'url_contains': {
        const currentUrl = window.location.href;
        return currentUrl.includes(String(value));
      }

      case 'url_matches': {
        const currentUrl = window.location.href;
        try {
          const regex = new RegExp(String(value));
          return regex.test(currentUrl);
        } catch (e) {
          this.log('Invalid regex in url_matches condition', e);
          return false;
        }
      }

      case 'device_type': {
        const deviceType = this.getDeviceType();
        return deviceType === value;
      }

      case 'referrer_contains': {
        const referrer = document.referrer;
        return referrer.includes(String(value));
      }

      case 'day_of_week': {
        const today = new Date().getDay(); // 0 = Sunday, 6 = Saturday
        const targetDays = String(value).split(',').map(d => parseInt(d.trim(), 10));
        return targetDays.includes(today);
      }

      case 'time_of_day': {
        // Value format: "HH:MM-HH:MM" (e.g., "09:00-17:00")
        const now = new Date();
        const currentMinutes = now.getHours() * 60 + now.getMinutes();
        const [start, end] = String(value).split('-');
        const [startH, startM] = start.split(':').map(n => parseInt(n, 10));
        const [endH, endM] = end.split(':').map(n => parseInt(n, 10));
        const startMinutes = startH * 60 + startM;
        const endMinutes = endH * 60 + endM;
        return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
      }

      default:
        this.log(`Unknown condition type: ${type}`);
        return true; // Unknown conditions pass by default
    }
  }

  /**
   * Compare two values with operator
   */
  private compareValues(actual: number, operator: string, target: number): boolean {
    switch (operator) {
      case '==':
      case '=':
        return actual === target;
      case '!=':
        return actual !== target;
      case '>':
        return actual > target;
      case '>=':
        return actual >= target;
      case '<':
        return actual < target;
      case '<=':
        return actual <= target;
      default:
        this.log(`Unknown operator: ${operator}`);
        return actual >= target;
    }
  }

  /**
   * Get device type
   */
  private getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
    const ua = navigator.userAgent;
    if (/Mobile|Android|iP(hone|od)/.test(ua)) {
      return 'mobile';
    }
    if (/Tablet|iPad/.test(ua)) {
      return 'tablet';
    }
    return 'desktop';
  }

  /**
   * Evaluate display flow for a popup
   * Returns true if the popup should be displayed
   */
  public evaluateDisplayFlow(popup: PopupConfig): boolean {
    const flow = popup.display_flow;

    // If no display flow defined, show the popup
    if (!flow || !flow.nodes || flow.nodes.length === 0) {
      this.log(`Popup ${popup.id} has no display flow, showing by default`);
      return true;
    }

    // Check if popup was already shown (unless configured to show multiple times)
    if (this.wasPopupShown(popup.id)) {
      this.log(`Popup ${popup.id} was already shown, skipping`);
      return false;
    }

    // Find the start node
    const startNode = flow.nodes.find(n => n.type === 'start');
    if (!startNode) {
      this.log(`Popup ${popup.id} has no start node, showing by default`);
      return true;
    }

    // Traverse the flow graph
    return this.traverseFlow(flow, startNode.id);
  }

  /**
   * Traverse flow graph from a node
   */
  private traverseFlow(
    flow: NonNullable<PopupConfig['display_flow']>,
    nodeId: string,
    visited: Set<string> = new Set()
  ): boolean {
    // Prevent infinite loops
    if (visited.has(nodeId)) {
      this.log(`Circular reference detected at node ${nodeId}`);
      return false;
    }
    visited.add(nodeId);

    const node = flow.nodes.find(n => n.id === nodeId);
    if (!node) {
      this.log(`Node ${nodeId} not found in flow`);
      return false;
    }

    this.log(`Evaluating node: ${node.type}`, node);

    switch (node.type) {
      case 'start':
        // Find the edge from start and follow it
        const startEdge = flow.edges.find(e => e.source === nodeId);
        if (!startEdge) {
          this.log('No edge from start node, showing by default');
          return true;
        }
        return this.traverseFlow(flow, startEdge.target, visited);

      case 'show_popup':
        // Reached show_popup node - popup should be displayed
        return true;

      case 'end':
      case 'do_not_show':
        // Reached end or do_not_show node - popup should not be displayed
        return false;

      // Condition nodes
      case 'page_views':
      case 'visitor_type':
      case 'time_on_page':
      case 'scroll_depth':
      case 'exit_intent':
      case 'url_contains':
      case 'url_matches':
      case 'device_type':
      case 'referrer_contains':
      case 'day_of_week':
      case 'time_of_day': {
        const condition: PopupCondition = {
          type: node.type,
          operator: node.operator,
          value: node.value,
        };
        const result = this.evaluateCondition(condition);
        this.log(`Condition ${node.type} evaluated to: ${result}`);

        // Find the appropriate edge based on result
        const edges = flow.edges.filter(e => e.source === nodeId);
        let nextEdge = edges.find(e =>
          (result && e.label === 'yes') ||
          (!result && e.label === 'no') ||
          (!e.label && result) // Default edge if no label
        );

        // If no matching edge found, try any edge
        if (!nextEdge && edges.length > 0) {
          nextEdge = edges[0];
        }

        if (!nextEdge) {
          this.log(`No edge from condition node ${nodeId}, result: ${result}`);
          return result; // Return the condition result if no edge
        }

        return this.traverseFlow(flow, nextEdge.target, visited);
      }

      default:
        this.log(`Unknown node type: ${node.type}`);
        // Try to follow any edge from this node
        const defaultEdge = flow.edges.find(e => e.source === nodeId);
        if (defaultEdge) {
          return this.traverseFlow(flow, defaultEdge.target, visited);
        }
        return true; // Show by default for unknown nodes
    }
  }

  /**
   * Get all visitor data (for debugging)
   */
  public getData(): VisitorData {
    return { ...this.data };
  }

  /**
   * Clear all tracking data
   */
  public clearData(): void {
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(this.key(key));
    });
    this.data = {
      pageViews: 0,
      firstVisit: Date.now(),
      lastVisit: Date.now(),
      sessionStart: Date.now(),
      popupsShown: {},
    };
    this.log('Visitor data cleared');
  }

  /**
   * Log message if debug mode enabled
   */
  private log(message: string, ...args: any[]): void {
    if (this.debug) {
      console.log(`[VisitorTracker] ${message}`, ...args);
    }
  }
}
